import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, ChevronRight, Home, FileText, DollarSign, Camera, Users, Handshake, ClipboardCheck, Key, AlertCircle, HelpCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

const CHRIS_PHOTO = "https://customer-assets.emergentagent.com/job_a0897f07-e635-4cd5-9eb9-21140ceaa71c/artifacts/drlh8cs0_brookebarclayphotography-1-99.jpg";

const TOC_ITEMS = [
  { id: "step-1", label: "Step 1: Deciding to Sell", icon: Home },
  { id: "step-2", label: "Step 2: Pricing Your Home", icon: DollarSign },
  { id: "step-3", label: "Step 3: Preparing & Disclosures", icon: FileText },
  { id: "step-4", label: "Step 4: Marketing & Listing", icon: Camera },
  { id: "step-5", label: "Step 5: Showings & Open Houses", icon: Users },
  { id: "step-6", label: "Step 6: Offers & Negotiation", icon: Handshake },
  { id: "step-7", label: "Step 7: Inspection & Appraisal", icon: ClipboardCheck },
  { id: "step-8", label: "Step 8: Closing", icon: Key },
  { id: "special-situations", label: "Special Situations", icon: AlertCircle },
  { id: "faq", label: "FAQ", icon: HelpCircle },
];

export default function SellerGuidePage() {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <>
      <Helmet>
        <title>Selling a Home in Grays Harbor: The Complete Guide | Chris Pierog</title>
        <meta name="description" content="Everything you need to know before you list, while you're under contract, and all the way through closing — written by Chris Pierog, Spivey Realty Group." />
        <meta name="keywords" content="sell home Grays Harbor, selling house Aberdeen WA, Grays Harbor real estate guide, home selling process, Chris Pierog realtor" />
      </Helmet>

      <div className="min-h-screen bg-[#f0efeb] flex flex-col" data-testid="seller-guide-page">
        <Header />
        
        <main className="flex-1 pt-20">
          {/* Hero Section */}
          <section className="bg-[#132723] text-white py-16 md:py-20">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <ChevronRight className="w-4 h-4" />
                <span>Seller Guide</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif leading-tight mb-6">
                Selling a Home in Grays Harbor:<br />
                <span className="text-white/80">The Complete Guide</span>
              </h1>
              
              <p className="text-lg text-white/80 mb-8 max-w-3xl">
                {"Everything you need to know before you list, while you're under contract, and all the way through closing — written by Chris Pierog, Spivey Realty Group."}
              </p>

              <div className="flex items-center gap-4">
                <img 
                  src={CHRIS_PHOTO} 
                  alt="Chris Pierog" 
                  className="w-14 h-14 rounded-full object-cover object-top border-2 border-white/20"
                />
                <div>
                  <p className="font-medium">Chris Pierog</p>
                  <p className="text-sm text-white/60">Licensed Realtor® | Spivey Realty Group</p>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Overview */}
          <section className="bg-white py-12 border-b border-[#e2e5e2]">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <h2 className="text-2xl font-serif text-[#132723] mb-4">Quick Overview: The Selling Timeline</h2>
              <p className="text-[#4d6153] mb-6">
                Selling a home in Grays Harbor generally takes 2–3 months from decision to closed sale, broken into three phases:
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-[#f0efeb] p-4">
                  <p className="font-semibold text-[#132723] mb-1">Prep & Listing</p>
                  <p className="text-sm text-[#4d6153]">1–3 weeks — deciding to sell, pricing, staging, disclosures, and going live</p>
                </div>
                <div className="bg-[#f0efeb] p-4">
                  <p className="font-semibold text-[#132723] mb-1">Under Contract</p>
                  <p className="text-sm text-[#4d6153]">~30 days — inspection, appraisal, negotiations, and escrow</p>
                </div>
                <div className="bg-[#f0efeb] p-4">
                  <p className="font-semibold text-[#132723] mb-1">Closing</p>
                  <p className="text-sm text-[#4d6153]">Signing, funding, and handing over keys</p>
                </div>
              </div>
              
              <p className="text-[#4d6153] text-sm">
                Below, we walk through every stage in detail. Jump to any section, or read start to finish.
              </p>
            </div>
          </section>

          {/* Table of Contents */}
          <section className="bg-[#f8f8f6] py-8 border-b border-[#e2e5e2] sticky top-16 md:top-20 z-30">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <p className="text-sm font-medium text-[#132723] mb-3">On this page:</p>
              <div className="flex flex-wrap gap-2">
                {TOC_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#e2e5e2] text-sm text-[#4d6153] hover:text-[#132723] hover:border-[#132723] transition-colors rounded-sm"
                  >
                    <item.icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{item.label}</span>
                    <span className="sm:hidden">{item.label.split(":")[0]}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Main Content */}
          <article className="py-12 md:py-16">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              
              {/* Step 1 */}
              <section id="step-1" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 1: Deciding to Sell
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    {"Before anything else, get clear on your \"why\" and your timeline — it changes almost every decision after this. Selling because of a job relocation with a hard deadline is a different strategy than selling because you're ready for the next chapter with no rush."}
                  </p>
                  <p className="font-medium text-[#132723] mt-6 mb-3">Questions worth answering first:</p>
                  <ul className="space-y-2 list-disc pl-5">
                    <li>Do you need to sell before you buy your next place, or can you buy first?</li>
                    <li>Is there a target move-out date driving the timeline?</li>
                    <li>Do you know roughly what you owe, and what you might net after selling?</li>
                  </ul>
                  <p className="text-sm text-[#4d6153]/70 mt-4 italic">
                    [Related: Renting vs. selling before you buy — coming soon]
                  </p>
                </div>
              </section>

              {/* Step 2 */}
              <section id="step-2" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 2: Pricing Your Home
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    {"This is the single highest-leverage decision in the entire process. Homes get the most buyer attention in the first 7–10 days on market — that's when your listing is freshest and pushed hardest by Zillow, the MLS, and search algorithms. Price it accurately from day one, and you catch that wave: strong showing activity, and often multiple interested buyers."}
                  </p>
                  <p>
                    {"Price it too high \"to leave room to negotiate,\" and the opposite happens. Serious buyers who know the market skip it. It sits. Eventually you're forced into a price reduction — which reads as a red flag to buyers (\"what's wrong with it?\") instead of a fresh, competitive listing."}
                  </p>
                  <div className="bg-[#132723] text-white p-6 my-6 -mx-2">
                    <p className="font-serif text-lg mb-2">How real pricing works:</p>
                    <p className="text-white/80">
                      {"It's based on a Comparative Market Analysis (CMA) — actual recent sales of similar homes nearby, not what you put into it, not what a neighbor claims theirs is worth, and not a generic online estimate. A real CMA accounts for condition, updates, lot, and current inventory levels specific to your neighborhood."}
                    </p>
                  </div>
                  <p className="text-sm text-[#4d6153]/70 italic">
                    [Related: The $50,000 pricing mistake most sellers make — coming soon]
                  </p>
                </div>
              </section>

              {/* Step 3 */}
              <section id="step-3" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 3: Preparing & Disclosures
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <h3 className="text-xl font-serif text-[#132723] mt-6 mb-4">Getting the home ready</h3>
                  <p>
                    {"You don't need a full renovation to compete. The highest-return prep work is almost always:"}
                  </p>
                  <ul className="space-y-3 list-none pl-0 my-6">
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Curb appeal</span> — buyers form a subconscious impression in the first 8 seconds, before they even get out of the car. Mowing, edging, fresh mulch, a power-washed walkway, and a clean front door area punch far above their cost.
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Kitchen presentation</span> — clear counters, updated hardware or a fresh coat of cabinet paint, and modern lighting fixtures make a disproportionate difference in how &quot;move-in ready&quot; a home feels.
                    </li>
                    <li className="bg-[#f0efeb] p-4">
                      <span className="font-semibold text-[#132723]">Decluttering and depersonalizing</span> — buyers need to picture their own life in the space, not yours.
                    </li>
                  </ul>

                  <h3 className="text-xl font-serif text-[#132723] mt-8 mb-4">{"Washington's required seller disclosure (Form 17)"}</h3>
                  <p>
                    {"Washington law requires almost every residential seller to complete a Seller Disclosure Statement — universally known as Form 17 — disclosing known material defects and other property condition details, under RCW 64.06. This must be delivered to the buyer within 5 business days of mutual acceptance of the purchase agreement, and once they receive it, the buyer has 3 business days to review it and potentially rescind the agreement."}
                  </p>
                  <p className="font-medium text-[#132723] mt-6 mb-3">A few things worth knowing about Form 17:</p>
                  <ul className="space-y-2 list-disc pl-5">
                    <li>{"It's based on your actual knowledge — you're not required to investigate or guess, only disclose what you know."}</li>
                    <li>{"Selling \"as-is\" does not remove your duty to disclose known material defects — that's a common misconception."}</li>
                    <li>{"If you order a pre-inspection before listing, anything it finds becomes something you now \"know\" and must disclose — this is a strategic decision worth discussing before you order one."}</li>
                  </ul>
                  <p className="text-sm text-[#4d6153]/70 mt-4 italic">
                    [Related: Form 17 explained line by line — coming soon]<br />
                    [Related: Should you get a pre-inspection before you list? — coming soon]
                  </p>
                </div>
              </section>

              {/* Step 4 */}
              <section id="step-4" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 4: Marketing & Listing
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    This is where most agents stop putting in effort — and it&apos;s exactly where Spivey Realty Group does the opposite. A real marketing plan for your listing includes:
                  </p>
                  <ul className="space-y-3 list-none pl-0 my-6">
                    <li className="flex items-start gap-3 bg-[#f0efeb] p-4">
                      <Camera className="w-5 h-5 text-[#00453d] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[#132723]">Professional in-house photography</span> — not iPhone photos. Buyers scroll past weak photos in under a second.
                      </div>
                    </li>
                    <li className="flex items-start gap-3 bg-[#f0efeb] p-4">
                      <FileText className="w-5 h-5 text-[#00453d] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[#132723]">A dedicated marketing team</span> building out professional copy and video, telling the story of your home rather than a bare-bones MLS description.
                      </div>
                    </li>
                    <li className="flex items-start gap-3 bg-[#f0efeb] p-4">
                      <Users className="w-5 h-5 text-[#00453d] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[#132723]">A content manager</span> actively getting your listing in front of buyers through social media and targeted online exposure — not just sitting on the MLS hoping someone finds it.
                      </div>
                    </li>
                    <li className="flex items-start gap-3 bg-[#132723] text-white p-4">
                      <Home className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold">Zillow Showcase</span> — an exclusive partnership currently unavailable to any other agent in Grays Harbor. It gives your listing a completely different, more interactive look on Zillow: clickable floor plans, AI staging shown in multiple styles for empty or dated rooms, and auto-playing video — all of which drive more clicks and more genuine interest than a standard listing.
                      </div>
                    </li>
                    <li className="flex items-start gap-3 bg-[#f0efeb] p-4">
                      <FileText className="w-5 h-5 text-[#00453d] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[#132723]">Neighborhood postcards</span> — because sometimes your buyer is two streets away.
                      </div>
                    </li>
                    <li className="flex items-start gap-3 bg-[#f0efeb] p-4">
                      <Users className="w-5 h-5 text-[#00453d] mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-[#132723]">Promoted open houses</span> — not just a sign in the yard on a random Sunday.
                      </div>
                    </li>
                  </ul>
                  <p className="text-sm text-[#4d6153]/70 italic">
                    [Related: What actually happens behind your listing — coming soon]
                  </p>
                </div>
              </section>

              {/* Step 5 */}
              <section id="step-5" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 5: Showings & Open Houses
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    Once live, your home will get scheduled showings and, typically, one or more open houses in the first couple weeks. A few things that make a real difference here:
                  </p>
                  <ul className="space-y-2 list-disc pl-5 my-6">
                    <li><strong>Keep the home &quot;show ready&quot;</strong> as much as possible during the first 1–2 weeks — this is your highest-traffic window.</li>
                    <li><strong>Leave during showings</strong> when possible; buyers are more candid and comfortable without the seller present.</li>
                    <li><strong>Open houses</strong> serve two purposes: giving buyers without an agent yet an easy way in, and creating visible activity that signals demand.</li>
                  </ul>
                </div>
              </section>

              {/* Step 6 */}
              <section id="step-6" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 6: Offers & Negotiation
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    {"When an offer comes in, you're not just looking at price — you're evaluating the whole package:"}
                  </p>
                  <ul className="space-y-2 list-disc pl-5 my-6">
                    <li><strong>Financing type</strong> — cash, conventional, FHA, VA — and how strong the buyer&apos;s approval actually is (fully underwritten is a much stronger position than simply pre-qualified)</li>
                    <li><strong>Contingencies</strong> — inspection, financing, appraisal, and sometimes a home-sale contingency (see Special Situations below)</li>
                    <li><strong>Closing timeline</strong> — does it match what you need?</li>
                    <li><strong>Earnest money amount</strong> — a signal of buyer seriousness</li>
                  </ul>
                  <p>
                    You can accept, counter, or reject any offer. In a multiple-offer situation, you&apos;re comparing all of the above across every offer, not just the top price.
                  </p>
                  <p className="text-sm text-[#4d6153]/70 mt-4 italic">
                    [Related: How to evaluate a contingent offer — coming soon]<br />
                    [Related: What &quot;fully underwritten&quot; actually means for a buyer&apos;s offer — coming soon]
                  </p>
                </div>
              </section>

              {/* Step 7 */}
              <section id="step-7" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 7: Under Contract — Inspection & Appraisal
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <h3 className="text-xl font-serif text-[#132723] mt-6 mb-4">Inspection</h3>
                  <p>
                    Most buyers will schedule a professional home inspection within the timeline set in the purchase agreement. Based on findings, they may ask you to make repairs, offer a credit, reduce the price, or (rarely) walk away if the contract allows it. This is a negotiation, not an obligation to fix everything requested.
                  </p>

                  <h3 className="text-xl font-serif text-[#132723] mt-8 mb-4">Appraisal</h3>
                  <p>
                    {"If the buyer is financing, their lender will require an appraisal to confirm the home supports the loan amount. Most of the time, this goes smoothly. When it doesn't — see Special Situations below for exactly what happens with a low appraisal."}
                  </p>
                </div>
              </section>

              {/* Step 8 */}
              <section id="step-8" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Step 8: Closing
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    Closing in Washington is typically handled through escrow at a title company, not by attorneys directly (though a real estate attorney can be brought in if needed for anything unusual). Around closing, you can expect to:
                  </p>
                  <ul className="space-y-2 list-disc pl-5 my-6">
                    <li><strong>{"Pay Washington's Real Estate Excise Tax (REET)"}</strong> — a state and local transfer tax on the sale, which is a seller-paid cost and becomes a lien on the property if unpaid</li>
                    <li><strong>Pay your share of prorated property taxes</strong>, escrow fees, and the owner&apos;s title insurance policy</li>
                    <li><strong>Pay agent commission</strong> (the specific structure is something we&apos;ll discuss directly and outline in your listing agreement)</li>
                    <li><strong>Sign the Statutory Warranty Deed</strong> transferring title to the buyer</li>
                  </ul>
                  <p>
                    Once everything is signed and funds are received and recorded, the sale is complete and keys are handed over.
                  </p>
                  <p className="text-sm text-[#4d6153]/70 mt-4 italic">
                    [Related: What actually costs money at closing, and what your net proceeds really look like — coming soon]
                  </p>
                </div>
              </section>

              {/* Special Situations */}
              <section id="special-situations" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  Special Situations
                </h2>
                <div className="prose prose-lg max-w-none text-[#4d6153]">
                  <p>
                    Every sale has the potential for something outside the standard path. Here&apos;s what to know about the most common ones:
                  </p>

                  <div className="bg-[#f0efeb] p-6 my-6">
                    <h3 className="text-lg font-serif text-[#132723] mb-3">Low Appraisal</h3>
                    <p className="text-[#4d6153] mb-0">
                      {"If the home appraises below the agreed purchase price, the buyer's lender won't lend more than the appraised value. Options include: the buyer covering the gap in cash, renegotiating the price, disputing the appraisal with additional comps, or (if the contract includes an appraisal contingency) either party may be able to walk away. This is negotiable, not automatically a dead deal."}
                    </p>
                  </div>

                  <div className="bg-[#f0efeb] p-6 my-6">
                    <h3 className="text-lg font-serif text-[#132723] mb-3">Wells & Septic Systems</h3>
                    <p className="text-[#4d6153] mb-0">
                      Many Grays Harbor properties are on private wells and septic systems rather than city water/sewer. Buyers financing these homes will often need well flow tests and septic inspections as part of their due diligence, and lenders may have specific requirements. This is common in our market and manageable with the right expectations set early — not something to be nervous about.
                    </p>
                  </div>

                  <div className="bg-[#f0efeb] p-6 my-6">
                    <h3 className="text-lg font-serif text-[#132723] mb-3">Contingent Offers</h3>
                    <p className="text-[#4d6153] mb-0">
                      {"A contingent offer means the buyer's purchase depends on something else happening first — most often, selling their own current home. These can absolutely be accepted, but they carry more uncertainty and timeline risk than a non-contingent offer, and are usually evaluated alongside a \"kick-out clause\" that lets you keep marketing the home or accept a better offer if one comes along."}
                    </p>
                  </div>

                  <div className="bg-[#f0efeb] p-6 my-6">
                    <h3 className="text-lg font-serif text-[#132723] mb-3">Off-Market / Pocket Listings</h3>
                    <p className="text-[#4d6153] mb-0">
                      {"Occasionally a home sells without ever being fully marketed on the open MLS — through direct agent networks or an existing buyer relationship. This can work in specific situations, but it usually means fewer eyes on your home, which typically works against getting top dollar. Worth a direct conversation about whether it fits your specific situation."}
                    </p>
                  </div>

                  <p className="text-sm text-[#4d6153]/70 italic">
                    [Related: Every one of these situations gets its own full breakdown — check the links above as they go live]
                  </p>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-16 scroll-mt-40">
                <h2 className="text-2xl md:text-3xl font-serif text-[#132723] mb-6 pb-3 border-b border-[#e2e5e2]">
                  FAQ
                </h2>
                <div className="space-y-6">
                  <div className="border-b border-[#e2e5e2] pb-6">
                    <h3 className="font-semibold text-[#132723] mb-2">How long does selling a home in Grays Harbor typically take?</h3>
                    <p className="text-[#4d6153]">
                      Plan on roughly 2–3 months from listing to closing — 1 to 3 weeks to prep and go live, and about 30 days in escrow once under contract, though this varies with market conditions.
                    </p>
                  </div>
                  <div className="border-b border-[#e2e5e2] pb-6">
                    <h3 className="font-semibold text-[#132723] mb-2">Do I have to disclose known issues if I sell &quot;as-is&quot;?</h3>
                    <p className="text-[#4d6153]">
                      {"Yes. Selling as-is affects repair negotiations, not your legal disclosure obligations under Washington's Form 17 requirement."}
                    </p>
                  </div>
                  <div className="border-b border-[#e2e5e2] pb-6">
                    <h3 className="font-semibold text-[#132723] mb-2">What if I need to sell and buy at the same time?</h3>
                    <p className="text-[#4d6153]">
                      This is common and manageable — it&apos;s a timing and contingency conversation worth having directly, since the right approach depends on your specific situation.
                    </p>
                  </div>
                  <div className="pb-6">
                    <h3 className="font-semibold text-[#132723] mb-2">Do I need a real estate attorney?</h3>
                    <p className="text-[#4d6153]">
                      Not required for a standard Washington sale, since escrow/title handles the closing documents — but one can be brought in for anything unusual in the transaction.
                    </p>
                  </div>
                </div>
              </section>

              {/* CTA Section */}
              <section className="bg-[#132723] text-white p-8 md:p-12 text-center">
                <h2 className="text-2xl md:text-3xl font-serif mb-4">Ready to Talk Specifics?</h2>
                <p className="text-white/80 mb-8 max-w-2xl mx-auto">
                  Every home and every situation is different — this guide covers how the process generally works, but the details of your sale deserve a direct conversation.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                  <Button
                    asChild
                    className="bg-white text-[#132723] hover:bg-[#f0efeb] px-8 py-6 text-lg font-medium"
                  >
                    <a 
                      href="https://homevalue.graysharborrealtor.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Get Your Free Home Value
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                  </Button>
                  
                  <a 
                    href="tel:+13605810167"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white text-white hover:bg-white hover:text-[#132723] transition-colors font-medium"
                  >
                    <Phone className="w-4 h-4" />
                    (360) 581-0167
                  </a>
                </div>

                <div className="flex items-center justify-center gap-4">
                  <img 
                    src={CHRIS_PHOTO} 
                    alt="Chris Pierog" 
                    className="w-12 h-12 rounded-full object-cover object-top border-2 border-white/20"
                  />
                  <div className="text-left">
                    <p className="font-medium">Chris Pierog</p>
                    <p className="text-sm text-white/60">Spivey Realty Group</p>
                  </div>
                </div>
              </section>

            </div>
          </article>
        </main>
        
        <Footer />
      </div>
    </>
  );
}
